// MA-73a-cap — additive DOM hooks the NON-LLM live-QA capture driver reads.
//
// WHY (ADR-045 R1 / ADR-047): the merge/revert gate consumes an `observation`.
// If that observation were agent-authored, an agent that can't reach the surface
// could hand-author a clean PASS/FAIL. These two inert attributes move the trust
// anchors OUT of the agent's hands and INTO the deployed page:
//
//   • data-capture-nonce — R1 nonce ECHO. The driver navigates
//     `<url>?qaNonce=<nonce>` and re-reads this attribute; the gate's
//     verifyCaptureProvenance() only blesses a capture where the page ECHOED the
//     harness's own nonce (pageEchoedNonce === expectedNonce). A page that never
//     rendered our nonce cannot match → PARK.
//
//   • data-build-sha — R2 head-SHA binding. The commit sha the page was actually
//     built from; the gate binds each judged sample to the deployed build so a
//     stale/cached/rolled-back page is not judged as the head-of-main build
//     (MA-73i guard-12 reads deployedBuildSha off this hook on PROD pages).
//
// SECURITY DESIGN (state here for the panel):
//
//   • The NONCE echo is ATTACKER-CONTROLLABLE reflected input (?qaNonce= in the
//     URL). So captureNonceAttr() is DOUBLE-gated: it echoes ONLY on non-prod
//     deploys (the SAME qaRoutesEnabled() gate /qa-item uses) AND only when the
//     value is a strict [A-Za-z0-9_-]{8,64} token. On prod, or for any malformed
//     nonce, it returns undefined → React omits the attribute entirely → no
//     reflected value ever renders on a production page. This is a defence: an
//     un-sanitised reflected attribute is an injection surface, and echoing a
//     nonce on prod would leak an internal capture handshake to the public app.
//
//   • data-build-sha ships on ALL envs INCLUDING PROD. This is INTENTIONAL and
//     the opposite of the nonce: a commit sha is INERT, non-attacker-controllable
//     build metadata (baked at build time from the Vercel env var, not read from
//     the URL). MA-73i guard-12 must read deployedBuildSha from the PROD page to
//     detect a stale/rolled-back prod deploy; a preview-only build-sha hook would
//     PERMANENTLY BLIND the CI revert net on the exact surface it must protect.
//
// Both attributes live on ONE inert `<div hidden aria-hidden>` mounted as the
// first child of the root provider in App.jsx, so they render on EVERY route and
// gate state (login-walled, error, loading, session) — the driver can read the
// build sha without authenticating or reaching any kid surface.

import { qaRoutesEnabled } from "./qaRoutes.ts";

// R1: a capture nonce is a short opaque token. Strict allow-list — reflected
// input, so anything outside this shape is refused (returns undefined) rather
// than echoed. 8–64 chars keeps it long enough to be unguessable, bounded so a
// megabyte of reflected junk can never land in the DOM.
const NONCE_RE = /^[A-Za-z0-9_-]{8,64}$/;

/**
 * R1 nonce echo (PREVIEW-ONLY, SANITISED). Returns the `?qaNonce=` value to
 * echo into `data-capture-nonce`, or undefined so the attribute is omitted.
 *
 * Returns undefined unless BOTH hold:
 *   1. qaRoutesEnabled() — the SAME non-prod (env + hostname) gate /qa-item uses,
 *      so a production page NEVER echoes a nonce.
 *   2. the `?qaNonce=` param matches /^[A-Za-z0-9_-]{8,64}$/ — attacker-
 *      controllable reflected input is strictly validated before it renders.
 */
export function captureNonceAttr(): string | undefined {
  // Preview/dev only — never echo reflected input on production.
  if (!qaRoutesEnabled()) return undefined;
  // No window (SSR/test without DOM) → nothing to reflect.
  if (typeof window === "undefined" || !window.location) return undefined;
  let nonce: string | null = null;
  try {
    nonce = new URLSearchParams(window.location.search).get("qaNonce");
  } catch {
    return undefined; // malformed search string → fail-closed (omit).
  }
  if (nonce === null) return undefined;
  if (!NONCE_RE.test(nonce)) return undefined; // sanitise before echo.
  return nonce;
}

/**
 * R2 build-SHA (ALL ENVS incl. PROD — see header rationale). Reads the Vercel-
 * exposed commit sha the same way Sentry's release tag does
 * (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA; see src/lib/sentry.ts). MA-73a-cap
 * (P1-1): vite.config.js now BAKES this literal from VERCEL_GIT_COMMIT_SHA at build
 * time, so prod pages render the real 40-hex sha even when Vercel's "auto-expose
 * system env vars" setting is off. The "dev" fallback therefore fires ONLY for
 * genuinely-local dev (empty string → "dev"); on any Vercel build it is the real
 * sha. The attribute is always present and non-empty (the gate treats a
 * missing/empty sha as EMPTY_CAPTURE → fail-closed).
 */
export function buildShaAttr(): string {
  const sha = import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA;
  return typeof sha === "string" && sha.length > 0 ? sha : "dev";
}

/**
 * MA-99 — auth-state ECHO (ALL ENVS incl. PROD — INERT, non-attacker-controllable).
 *
 * The prod live-QA `treeCoherence` capture (MA-74/76) needs an AUTHENTICATED
 * session — the SkillTree lives behind Google login + the parent gate. The
 * supervised MA-53 drill loads a DEDICATED QA-account Playwright storageState
 * (`--storage-state`, MA-99) so the driver reaches `/parent/tree` authed.
 *
 * ⭐WHY THIS HOOK EXISTS (PURPLE-15, ADR-048): before MA-99 the capture driver
 * HARDCODED `authState: "google-authed"` for every prod capture — a LATENT LIE.
 * An expired/invalid/absent session renders the login wall, but the driver would
 * still attest `google-authed`, so the judge could not tell "authed capture" from
 * "login-walled capture". This hook moves the auth signal OUT of the driver's
 * assumptions and INTO the deployed page: the driver READS it, and the gate bands
 * an expired session to ABSTAIN (never a false PASS/FAIL) — see
 * captureDriverCore.assembleObservation + checkEngine.trustGate.
 *
 * INERT + SAFE on prod (like data-build-sha, unlike the nonce): the value is
 * derived from the app's OWN auth context (a boolean "is there an authed user"),
 * NOT from any URL/attacker-controllable input, and it leaks nothing — "is a user
 * logged in" is not sensitive and no identity/token is exposed. It is `"google-authed"`
 * ONLY when a real authed user object is present (the app's sole auth path is Google
 * OAuth — src/supabaseClient.js signInWithGoogle), else `"anon"`.
 *
 * @param user the auth-context user (null/undefined when logged out).
 */
export function authStateAttr(user: { id?: string | null } | null | undefined): string {
  return user && typeof user.id === "string" && user.id.length > 0 ? "google-authed" : "anon";
}

/**
 * MA-CLOSURE6 (closure-instrument slice 6, MA-550) — front-door landing
 * bucket ECHO (ALL ENVS incl. PROD — INERT, same class as authStateAttr
 * above, never build-sha's "sensitive but non-secret" or the nonce's
 * "attacker-controllable" categories). MA-550's live-QA scenario dispatches
 * the bare front door ("/") with a fresh browser context (pw_user_role=
 * "teacher", pw_device_mode never set — see captureDriver.mjs's
 * shouldStampFrontDoorTeacherUnset) and needs to know, from the served page
 * alone, which of the closed {"teacher","play","other"} buckets the CLIENT-
 * SIDE redirect actually landed on. `capture.finalPathname` (page.url() at
 * capture time) already carries the real pathname, but a bucketed, closed-
 * enum DOM echo lets the driver's own read stay a plain single-attribute
 * fold (SCENARIO_PROBE_KEYS' generic convention) instead of a bespoke
 * pathname parse inside the page-evaluated read body. Reflects ONLY the
 * CURRENT document's own pathname (never a URL param, never anything
 * attacker-supplied) — no injection surface, nothing sensitive (a route
 * bucket name is not PII).
 *
 * @param pathname the current document's pathname (window.location.pathname
 *   at the call site; "" when unavailable, e.g. SSR/test-without-DOM).
 */
export function frontDoorLandingAttr(pathname: string): "teacher" | "play" | "other" {
  if (pathname === "/teacher") return "teacher";
  if (pathname === "/play") return "play";
  return "other";
}

/**
 * MA-684 (MA-550 false-PASS fix, run 35499315272) — the PAGE-SIDE
 * CONFIRMATION that the qaLocal init script actually ran BEFORE this
 * document rendered. WHY: run 35499315272 captured /teacher DIRECTLY with
 * bare `{"scenarioProbes":true}` (the MA-542 dispatch, no qaLocal at all)
 * and still PASSed MA-550, because frontDoorLandingAttr (above) classifies
 * whatever pathname the document CURRENTLY has, regardless of how it got
 * there or whether the front-door redirect ever ran. This hook closes that
 * hole from the OTHER end: it reads back the two localStorage keys
 * captureDriver.mjs's shouldStampQaLocal("teacher-unset") is supposed to
 * have set (pw_user_role="teacher", pw_device_mode REMOVED) at the exact
 * moment this component first renders — i.e. after Playwright's
 * addInitScript has run (addInitScript executes before ANY page script,
 * including this one) but before the app can have changed either key
 * itself. A capture that skipped the qaLocal stamp (a bare dispatch, or
 * any dispatch other than "teacher-unset") reads back "other" here even if
 * it happens to be rendering on /teacher for an unrelated reason — so this
 * signal cannot be satisfied by navigating to the right URL alone, only by
 * the init script genuinely having run.
 *
 * Closed three-value set, mirroring frontDoorLandingAttr's own shape:
 *   - "teacher-unset": userRoleRaw === "teacher" AND deviceModeRaw is
 *     absent (null/undefined) — the exact never-configured-device shape
 *     MA-550's own dispatch is supposed to produce.
 *   - "teacher-set": userRoleRaw === "teacher" but deviceModeRaw is
 *     PRESENT (some earlier session — same throwaway CI context — left a
 *     device-mode value behind, or the app itself has already written
 *     one). Distinct from "teacher-unset": MA-550's very premise is a
 *     device with NO stored mode, so this must never be treated the same.
 *   - "other": anything else (role not "teacher", or role absent/garbage)
 *     — including every ordinary non-MA-550 capture, which never applies
 *     this stamp at all.
 *
 * @param userRoleRaw raw localStorage.getItem("pw_user_role") at render
 *   time (null when absent/unavailable, e.g. SSR/private mode).
 * @param deviceModeRaw raw localStorage.getItem("pw_device_mode") at
 *   render time (null when absent/unavailable).
 */
export function qaLocalInitAttr(
  userRoleRaw: string | null,
  deviceModeRaw: string | null,
): "teacher-unset" | "teacher-set" | "other" {
  if (userRoleRaw !== "teacher") return "other";
  return deviceModeRaw == null ? "teacher-unset" : "teacher-set";
}
